import React, { useCallback, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import HarbourIconButton from './HarbourIconButton';
import HarbourPathwayMark from './HarbourPathwayMark';
import HarbourTextLink from './HarbourTextLink';
import './harbour.css';

/*
 * Harbour/Flyout: the record and task shell that opens over the pupils page.
 * Width "record" (1080, right-aligned) holds the pupil record; "wide" (1688)
 * holds task screens — wizards, school forms homepages, questions and review.
 *
 * Header variants (Harbour/Flyout header):
 * - title: pupil name + optional year — the record flyout.
 * - task:  pathway eyebrow (mark + label) above the name, optional help line —
 *          wizards and forms homepages.
 * - back:  a compact "back" text link only — question and review screens.
 * All variants end with the outlined Close button.
 *
 * Built on Radix Dialog for the focus trap, Escape handling and scroll lock.
 *
 * While the content is scrolled away from the top, the header takes a soft
 * shadow so it reads as sitting over the content (TDI-697, 4223:305310);
 * back at the top, or on a record too short to scroll, it has none.
 */
export default function HarbourFlyout({
  open = true,
  width = 'record',
  title,
  titleSize = 'display', // 'display' (pupil name, 48) | 'heading' (task title, 32)
  year,
  eyebrow, // { tone: 'sen'|'referral', label, tag? } — tag: a second marked label beside it
  help,
  backLink, // { label, onClick }
  onClose,
  closeLabel = 'Close',
  children,
}) {
  const [scrolled, setScrolled] = useState(false);
  const onContentScroll = useCallback((e) => setScrolled(e.currentTarget.scrollTop > 0), []);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && onClose) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="harbour harbour-scrim" />
        <Dialog.Content
          className={`harbour harbour-flyout harbour-flyout--${width}`}
          aria-describedby={undefined}
        >
          <div className={`harbour-flyout__header${scrolled ? ' harbour-flyout__header--scrolled' : ''}`}>
            <div className="flex flex-col gap-1 min-w-0">
              {backLink ? (
                <Dialog.Title asChild>
                  <span className="sr-only">{title}</span>
                </Dialog.Title>
              ) : (
                <>
                  {eyebrow && (
                    <span className="flex items-center gap-6 flex-wrap">
                      <span className={`harbour-flyout__eyebrow harbour-flyout__eyebrow--${eyebrow.tone}`}>
                        {(eyebrow.tone === 'sen' || eyebrow.tone === 'referral') && (
                          <HarbourPathwayMark pathway={eyebrow.tone} />
                        )}
                        {eyebrow.label}
                      </span>
                      {eyebrow.tag && (
                        <span className={`harbour-flyout__eyebrow harbour-flyout__eyebrow--${eyebrow.tone}`}>
                          {(eyebrow.tone === 'sen' || eyebrow.tone === 'referral') && (
                            <HarbourPathwayMark pathway={eyebrow.tone} />
                          )}
                          {eyebrow.tag}
                        </span>
                      )}
                    </span>
                  )}
                  <Dialog.Title asChild>
                    <h1 className="harbour-flyout__who">
                      <span className={titleSize === 'heading' ? 'harbour-heading-32' : 'harbour-flyout__name'}>
                        {title}
                      </span>
                      {year && <span className="harbour-flyout__year">{year}</span>}
                    </h1>
                  </Dialog.Title>
                  {help && <p className="harbour-flyout__help">{help}</p>}
                </>
              )}
              {backLink && (
                <HarbourTextLink leadingIcon="arrow-left" onClick={backLink.onClick}>
                  {backLink.label}
                </HarbourTextLink>
              )}
            </div>
            <div className="ml-auto flex-none">
              <HarbourIconButton icon="close" label={closeLabel} onClick={onClose} />
            </div>
          </div>
          <div className="harbour-flyout__content" onScroll={onContentScroll}>{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
